import { DebitType } from '../../../../types/DebitType';

type IntervalType = 'weekly' | 'monthly' | 'daily' | 'callOff';

/**
 * Translates an interval type from English to Swedish.
 *
 * Converts interval type codes to their Swedish display names for UI presentation.
 *
 * @param intervalType - The interval type to translate, or undefined
 * @returns Swedish translation of the interval type, or empty string if undefined/unknown
 *
 * @example
 * ```typescript
 * translateIntervalType('weekly') // Returns 'Veckovis'
 * translateIntervalType('callOff') // Returns 'Avrop'
 * translateIntervalType(undefined) // Returns ''
 * ```
 */
export const translateIntervalType = (intervalType: IntervalType | undefined): string => {
  switch (intervalType) {
    case 'weekly':
      return 'Veckovis';
    case 'monthly':
      return 'Månadsvis';
    case 'daily':
      return 'Dagligen';
    case 'callOff':
      return 'Avrop';
    default:
      return '';
  }
};

/**
 * The interval type as shown to the user. A one-time interval is stored with a daily or weekly
 * intervalType, so it is told apart by its debit type.
 */
export const intervalTypeLabel = (interval: {
  intervalType?: IntervalType;
  debitType?: string | null;
}): string =>
  interval.debitType === DebitType.OneTime
    ? 'En gång'
    : translateIntervalType(interval.intervalType);

/**
 * Maps an interval type to the number of occurrences per year.
 * Used in `calculateIntervalSum` to annualise the per-occurrence sum before
 * dividing back to the correct billing period.
 *   weekly  → 52
 *   monthly → 12
 *   daily   → 365
 *   callOff → 1 (on-demand; no annualisation)
 */
export const IntervalTypeTranslationForCalculation = (
  intervalType: IntervalType | undefined
): number => {
  switch (intervalType) {
    case 'weekly':
      return 52;
    case 'monthly':
      return 12;
    case 'daily':
      return 365;
    case 'callOff':
      return 1;
    default:
      return 1;
  }
};

export const intervalTypes = [
  { title: 'Dagligen', value: 'daily' },
  { title: 'Månadsvis', value: 'monthly' },
  { title: 'Veckovis', value: 'weekly' },
  { title: 'Avrop', value: 'callOff' },
];

/**
 * Defines which intervalTypes are selectable for each debitType.
 * The restriction prevents nonsensical combinations, e.g. a Monthly-billed
 * interval cannot use callOff (which has no regular cadence).
 */
export const allowedIntervalTypesByDebitType: Record<DebitType, string[]> = {
  [DebitType.Monthly]: ['monthly', 'weekly', 'daily'],
  [DebitType.Weekly]: ['weekly', 'daily'],
  [DebitType.OneTime]: ['weekly', 'daily'],
  [DebitType.Ongoing]: ['monthly', 'weekly', 'daily', 'callOff'],
  [DebitType.Unit]: ['monthly', 'weekly', 'daily', 'callOff'],
};
